<template>
  <div>
    <Grid
      @api="getOrders()"
      :headers="headers"
      :payload="{results, count, next, previous}"
      @filter="showFilterModal = true"
      @clearFilters="$refs.desktopFilters.reset()"
    >
      <template #filter>
        <OrderFilter ref="desktopFilters" />
      </template>

      <!-- Header -->
      <template #head.actions>
        <span class="bg-secondary"></span>
      </template>
      <template #head.check>
        <div class="bg-secondary text-left text-base">
          <base-check :checked="!!areAllVisibleOrdersSelected" @input="toggleSelectVisibleOrders" />
        </div>
      </template>
      <template #buttons>
        <base-button
          v-if="selectedOrdersAmount > 0"
          action="order_download"
          size="sm"
          :loading="pendingOrdersDownload"
          :disabled="pendingOrdersDownload"
          @click="downloadSelectedOrders"
        >
          <base-icon icon="download" class="mr-2" />
          {{ `${downloadBtnText} (${selectedOrdersAmount})` }}
        </base-button>
      </template>

      <!-- Rows -->
      <template #body.check="{item}">
        <div class="text-base">
          <base-check
            :checked="!!selectedOrders[item.delivery_order_number]"
            @input="$set(selectedOrders, item.delivery_order_number, $event)"
          />
        </div>
      </template>
      <template #body.delivery_order_number="{item}">
        <div class="flex flex-col">
          <div :class="['flex flex-col lg:flex-row lg:items-center leading-5 font-medium']">
            <span>{{ item.delivery_order_number }}</span>
            <router-link
              :to="{name: 'Keys', query: {delivery_order_number: item.delivery_order_number}}"
            >
              <base-button-icon
                action="view_key"
                tooltip="key_info"
                size="xs"
                class="ml-1 lg:ml-3 bg-primary-light hover:text-primary-light rounded text-white"
              />
            </router-link>
          </div>
        </div>
      </template>
      <template #body.client_order_number="{item}">
        <div class="flex flex-col">
          <div :class="['flex flex-col lg:flex-row lg:items-center leading-5']">
            <span class="truncate">{{ item.client_order_number }}</span>
          </div>
        </div>
      </template>
      <template #body.company__name="{item}">
        <div :class="['flex flex-col lg:flex-row space-x-6 leading-5 font-medium']">
          <!-- <span>{{ item.company.split('-')[1].trim() }}</span>
          <div v-if="isGwf" class="flex-shrink-0 lg:ml-2">
            <span
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded text-gray-600 bg-secondary block"
              >{{ item.company.split('-')[0].trim() }}</span
            >
          </div> -->
          <span
            class="px-2 inline-flex text-xs leading-5 font-semibold rounded text-gray-600 bg-secondary"
            >{{ item.company.split(' - ')[0] }}
          </span>
          <span>{{ item.company.split(' - ')[1] }} </span>
        </div>
      </template>
      <template #body.sales_representative="{item}">
        <div class="flex flex-col">
          <div :class="['flex flex-col lg:flex-row lg:items-center leading-5 font-medium']">
            <span
              class="truncate"
              v-tooltip="{
                content: item.sales_representative,
                placement: 'left',
              }"
              >{{ item.sales_representative }}</span
            >
          </div>
        </div>
      </template>
      <template #body.created_at="{item}">
        {{ item.created_at ? $d(new Date(item.created_at), 'long') : '-' }}
      </template>
      <template #body.actions="{item}">
        <div class="flex align-middle">
          <span v-if="ordersDownloadState[item[pk]]" class="self-center mr-4 w-4">
            <base-icon
              :class="ordersDownloadState[item[pk]].classes"
              :icon="ordersDownloadState[item[pk]].icon"
            />
          </span>
          <base-button-icon
            @click="toggleOrderDetails(item[pk])"
            tooltip="Shipment"
            class="bg-secondary-light"
            :class="{invisible: item.shipments.length === 0}"
            :icon="visibleOrders.includes(item[pk]) ? `expand-up` : `expand-down`"
          />
        </div>
      </template>

      <!-- Row details -->
      <template #expanded="{item}">
        <transition name="slidedown" class="slidedown transition-all">
          <div v-if="visibleOrders.includes(item[pk])">
            <div class="grid-expanded">
              <div class="flex flex-wrap items-center content-around text-sm text-gray-700">
                <div class="flex flex-col" v-for="shipment in item.shipments" :key="shipment.id">
                  <div class="mx-3 shadow bg-white p-3 my-2 lg:my-3">
                    <!-- <div class="mb-1 flex justify-between">
                      <span class="mr-4 lg:mr-8">{{ text('delivery_note_number') }}:</span
                      ><span class="tracking-wide">{{ shipment.delivery_note_number }}</span>
                    </div> -->
                    <div class="mb-2 flex justify-between">
                      <span class="mr-4 lg:mr-8">{{ text('serial_number') }}:</span
                      ><span class="tracking-wide">{{ shipment.device_serial }}</span>
                    </div>
                    <div class="border-t pt-2 flex justify-between text-gray-500">
                      <span>{{ $d(new Date(shipment.date), 'long') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </template>
    </Grid>

    <!-- Mobile filters -->
    <transition v-if="showFilterModal" name="grow">
      <base-modal @close="showFilterModal = false" head="filters" bg="white" full-width>
        <OrderFilter :mobile="true" ref="mobileFilter" />
        <!-- Action buttons -->
        <div class="pt-4 mt-6 border-t flex flex-col justify-end w-full">
          <div class="flex flex-col">
            <div class="flex space-x-4">
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.reset()
                  showFilterModal = false
                "
                action="clear"
                context="secondary"
              >
                <base-icon icon="close" />
              </base-button>
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.submit()
                  showFilterModal = false
                "
                action="submit"
              >
                <base-icon icon="filters" />
              </base-button>
            </div>
          </div>
        </div>
      </base-modal>
    </transition>
  </div>
</template>

<script>
import fileExport from '@/state/helpers/fileExport.js'
import OrderFilter from './OrderFilter'
import {BaseIcon, BaseCheck, BaseButton, BaseButtonIcon, BaseModal} from '@/components/base'
import Grid from '@/components/shared/Grid.vue'

import orderBy from 'lodash-es/orderBy'

export default {
  components: {
    Grid,
    BaseIcon,
    BaseCheck,
    BaseButton,
    BaseButtonIcon,
    OrderFilter,
    BaseModal,
  },

  data() {
    return {
      pendingOrdersDownload: false,
      list: {},
      pk: this.$store.getters['orders/pk'],
      visibleOrders: [],
      selectedOrders: {},
      ordersDownloadState: {},
      headers: {
        check: {
          label: '',
          order: false,
          className: 'w-10',
        },
        delivery_order_number: {
          label: 'delivery_order_number',
          order: true,
          className: 'w-2/12',
        },
        client_order_number: {
          label: 'client_order_number',
          order: true,
          className: 'w-2/12',
        },
        company__name: {
          label: 'company',
          order: true,
          className: 'w-3/12',
        },
        sales_representative: {
          label: 'sales_representative',
          order: true,
          className: 'w-1/12',
        },
        created_at: {
          label: 'created',
          order: true,
          className: 'w-2/12',
        },
        actions: {
          label: '',
          order: false,
          className: 'flex items-end justify-center w-16',
        },
      },
      showFilterModal: false,
    }
  },

  computed: {
    count() {
      return this.list && this.list.count ? this.list.count : 0
    },
    next() {
      return this.list && this.list.next ? this.list.next : null
    },
    previous() {
      return this.list && this.list.previous ? this.list.previous : null
    },
    results() {
      return this.list && this.list.results ? this.list.results : []
    },
    ordersSorted() {
      return this.$route.query.ordering
    },
    isGwf() {
      return (
        !this.$store.getters['auth/currentUser'].companies ||
        this.$store.getters['auth/currentUser'].companies.length === 0
      )
    },
    selectedOrdersAmount() {
      return Object.keys(this.selectedOrders).filter(o => this.selectedOrders[o]).length
    },
    areAllVisibleOrdersSelected() {
      if (!this.list || !this.list.results) {
        return false
      }
      return this.list.results.every(o => this.selectedOrders[o.delivery_order_number])
    },
    downloadBtnText() {
      return this.$t('mygwf.components.Button.order_download')
    },
    failedToDownload() {
      return `${this.$t('mygwf.components.Orders.failed_download')}`
    },
  },

  methods: {
    toggleSelectVisibleOrders(shouldSelect) {
      this.list.results.forEach(o =>
        this.$set(this.selectedOrders, o.delivery_order_number, shouldSelect)
      )
    },

    async getOrders() {
      try {
        this.list = await this.$store.dispatch('orders/get')
        if (!this.ordersSorted) {
          this.list.results = orderBy(
            this.list.results,
            [order => new Date(order.created_at)],
            ['desc']
          )
        }
      } catch (error) {}
    },

    toggleOrderDetails(id) {
      if (!this.visibleOrders.includes(id)) {
        return this.visibleOrders.push(id)
      }
      return this.visibleOrders.splice(this.visibleOrders.indexOf(id), 1)
    },

    async downloadSelectedOrders() {
      if (this.pendingOrdersDownload) {
        return
      }
      try {
        this.pendingOrdersDownload = true

        // Filters order to be downloaded
        const ordersToBeDownloaded = this.list.results.filter(
          o => this.selectedOrders[o.delivery_order_number]
        )
        if (ordersToBeDownloaded.length === 0) {
          return
        }

        for (let i = 0; i < ordersToBeDownloaded.length; i++) {
          const order = ordersToBeDownloaded[i]

          const taskId = await this.$store.dispatch('asyncTasksTracker/addTask', {
            status: 'pending',
            title: `Downloading order ${order.delivery_order_number}`,
          })
          this.$set(this.ordersDownloadState, order.delivery_order_number, {
            taskId,
            icon: 'spinner-btn',
            classes: 'icon-spin',
          })
        }

        // Download orders sequentially
        for (let i = 0; i < ordersToBeDownloaded.length; i++) {
          const order = ordersToBeDownloaded[i]
          const taskId = this.ordersDownloadState[order.delivery_order_number].taskId
          try {
            const file = await this.$store.dispatch('orders/getSalesOrder', order)
            const fileName = `${order.delivery_order_number}.pdf`
            fileExport(file, fileName)
            this.$set(this.ordersDownloadState, order.delivery_order_number, {
              taskId,
              icon: 'checkmark',
              classes: 'text-green-dark',
            })
            await this.$store.dispatch('asyncTasksTracker/updateTaskStatus', {
              id: taskId,
              status: 'complete',
            })
            await this.$store.dispatch('asyncTasksTracker/updateTaskTitle', {
              id: taskId,
              title: `Order ${order.delivery_order_number} downloaded`,
            })
          } catch (e) {
            this.$set(this.ordersDownloadState, order.delivery_order_number, {
              taskId,
              icon: 'close',
              classes: 'text-red-default',
            })
            await this.$store.dispatch('asyncTasksTracker/updateTaskStatus', {
              id: taskId,
              status: 'failed',
            })
            await this.$store.dispatch('asyncTasksTracker/updateTaskTitle', {
              id: taskId,
              title: this.failedToDownload + ' ' + `${order.delivery_order_number}`,
            })
          }
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.selectedOrders = {}
        this.pendingOrdersDownload = false
        setTimeout(() => (this.ordersDownloadState = {}), 3000)
      }
    },

    text(field) {
      return this.$t(`mygwf.components.Grid.${field}`)
    },
  },
}
</script>
